import { QueryCache, QueryClient, MutationCache } from '@tanstack/react-query';
import { ApiError, isUnauthenticated } from './client';
import { SESSION_QUERY_KEY } from '../session/session';

/**
 * One QueryClient for the app.
 *
 * Retries: only network failures and server errors (5xx), once. Client errors (including 401)
 * and malformed responses are never retried — repeating them cannot help.
 *
 * 401: any 401 from a query or mutation means the session has ended. The session is re-read once
 * from the API; when it confirms there is no session, every other cached result is dropped so no
 * protected data stays in memory, and the route guards send the user to /login. Auth-flow
 * mutations (sign-in, sign-out; `meta.authFlow`) are exempt: a 401 there means "wrong
 * credentials" or "already signed out", which those flows handle themselves.
 */
export function createQueryClient(): QueryClient {
  const client: QueryClient = new QueryClient({
    queryCache: new QueryCache({ onError: (error) => onUnauthenticated(error) }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        if (mutation.meta?.authFlow) return;
        onUnauthenticated(error);
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) =>
          failureCount < 1 &&
          error instanceof ApiError &&
          (error.code === 'network_error' || error.status >= 500),
      },
      mutations: { retry: false },
    },
  });

  function onUnauthenticated(error: unknown) {
    if (!isUnauthenticated(error)) return;
    void client.invalidateQueries({ queryKey: SESSION_QUERY_KEY }).then(() => {
      if (client.getQueryData(SESSION_QUERY_KEY) === null) {
        client.removeQueries({ predicate: (q) => q.queryKey[0] !== SESSION_QUERY_KEY[0] });
      }
    });
  }

  return client;
}
